import { AudienceLogicTypes } from '@/common/enums/audience-logic-types';
import { isExternalSegmentRule } from '@/common/enums/audience-rule-modes';
import AudienceNode, { IAudienceNode } from '@/common/models/audience/AudienceNode';
import AudienceRule from '@/common/models/audience/AudienceRule';

export const operatorTypes = [
  { label: 'AND', value: '&' },
  { label: 'OR', value: '|' },
];
type OperatorType = (typeof operatorTypes)[number];

export default class AudienceGroup implements IAudienceNode {
  parent?: AudienceGroup | null;
  isNegated: boolean;
  nodes: AudienceNode[];
  type = AudienceLogicTypes.Group;
  key: number;
  operator: OperatorType;
  isInternal: boolean = false;

  constructor(
    nodes: AudienceNode[] = [],
    parent: AudienceGroup = null,
    isNegated: boolean = false,
    operator: '|' | '&' = '|',
  ) {
    this.parent = parent;
    this.isNegated = isNegated;
    this.nodes = nodes;
    this.nodes.forEach((n) => (n.parent = this));
    if (operator) {
      this.operator = operatorTypes.find((o) => o.value === operator);
    }
  }

  get topParent() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let top: AudienceGroup = this;
    while (top.parent) {
      top = top.parent;
    }
    return top;
  }

  get hasSegmentRule() {
    return this.nodes.some((node) => {
      if (node.type === AudienceLogicTypes.Group) {
        return (node as AudienceGroup).hasSegmentRule;
      } else {
        const mode = (node as AudienceRule).mode;
        return isExternalSegmentRule(mode);
      }
    });
  }

  setOperator(operator: '|' | '&') {
    this.operator = operatorTypes.find((o) => o.value === operator);
  }

  addChild(...nodes: AudienceNode[]) {
    nodes.forEach((node) => {
      if (node.type === AudienceLogicTypes.Group) {
        const group = node as AudienceGroup;
        if (group.isInternal && group.operator === this.operator) {
          this.addChild(...group.nodes); // recurse on internal matching groups
          return;
        }
      }
      // base case
      this.nodes.push(node);
      node.parent = this;
    });
  }

  insertChild(idx: number, node: AudienceNode) {
    if (node.type === AudienceLogicTypes.Group) {
      const group = node as AudienceGroup;
      if (group.isInternal && group.operator === this.operator) {
        group.nodes.forEach((n) => (n.parent = this));
        this.nodes = [...this.nodes.slice(0, idx), ...group.nodes, ...this.nodes.slice(idx)];
        return;
      }
    }
    node.parent = this;
    this.nodes = [...this.nodes.slice(0, idx), node, ...this.nodes.slice(idx)];
  }

  removeChild(node: AudienceNode) {
    const idx = this.nodes.indexOf(node);
    this.nodes.splice(idx);
  }
}
